// client/src/pages/ClientesPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { getClientes, createCliente, updateCliente, deleteCliente } from "../api/clientes.js";
import { getRestaurantes } from "../api/restaurantes.js"; 
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";

const ClientesPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [clientes, setClientes] = useState([]);
    const [cliente, setCliente] = useState({
        nombre: "",
        correo: "",
        telefono: "",
        tipo_identificacion: "05",
        identificacion: "",
        direccion: "",
        tipo_cliente: "invitado",
        id_restaurante: "",
        id_usuario: "",
    });
    const [filteredClientes, setFilteredClientes] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const inputRefs = useRef({});
    const { sortedItems: sortedClientes, sortConfig, setSortConfig } = useSortableData(filteredClientes);
    // Hook de paginación
    const { currentPage, setCurrentPage, paginatedItems: currentClientes, totalPages, itemsPerPage } = usePagination(sortedClientes, 20);
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [restaurantes, setRestaurantes] = useState([]);

  // Cargar restaurantes
    useEffect(() => {
    const loadRestaurantes = async () => {
        try {
        const data = await getRestaurantes();
        setRestaurantes(Array.isArray(data) ? data : data.restaurantes || []);
        } catch (err) {
            console.error("Error al cargar restaurantes:", err);
        }
        };
        loadRestaurantes();
    }, []);

  // Cargar clientes
    const loadClientes = async () => {
        try {
        const data = await getClientes();
        setClientes(data);
        setFilteredClientes(data);
        } catch (err) {
        console.error("Error al cargar clientes:", err);
        }
    };

    useEffect(() => {
        loadClientes();
    }, []);

    // Filtro
    useEffect(() => {
        const filtered = clientes.filter(c =>
        Object.values(c).some(val =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
        );
        setFilteredClientes(filtered);
    }, [searchTerm, clientes]);

    const handleChange = (e) => {
        setCliente({ ...cliente, [e.target.name]: e.target.value });
    };

    const resetForm = () => {
        setCliente({
        nombre: "",
        correo: "",
        telefono: "",
        tipo_identificacion: "05",
        identificacion: "",
        direccion: "",
        tipo_cliente: "invitado",
        id_restaurante: usuarioLogueado?.isSuperAdmin ? "" : usuarioLogueado?.id_restaurante || "",
        });
        setErrors({});
        setEditMode(false);
    };

    const validateCliente = (data) => {
        const newErrors = {};
        if (!data.nombre.trim()) newErrors.nombre = "El nombre es obligatorio";
        if (!data.direccion.trim()) newErrors.direccion = "La dirección es obligatoria.";
        if (!data.identificacion.trim()) newErrors.identificacion = "La identificación es obligatoria";
        if (!data.tipo_identificacion) newErrors.tipo_identificacion = "Seleccione tipo de identificación";
        // Solo validar id_restaurante si es SuperAdmin
        if (usuarioLogueado?.isSuperAdmin && !data.id_restaurante) {
            newErrors.id_restaurante = "Seleccione un restaurante";
        }
        if (!data.telefono.trim()) newErrors.telefono = "El Teléfono es obligatoria.";
        else if (!/^\d{10}$/.test(data.telefono)) newErrors.telefono = "Debe tener 10 dígitos.";
        if (!data.correo.trim()) { newErrors.correo = "El correo es obligatorio."; } 
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.correo.trim())) { newErrors.correo = "El formato del correo no es válido."; }
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateCliente(cliente);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstErrorField = Object.keys(validationErrors)[0];
            const fieldRef = inputRefs.current[firstErrorField];
            if (fieldRef?.scrollIntoView) {
            fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
            fieldRef.focus();
            }
            return;
        }

        try {
            // Incluir id_usuario antes de enviar al backend
            const clienteConUsuario = {
            ...cliente,
            id_usuario: usuarioLogueado?.id_usuario || null,
            };
            
            // Si no es SuperAdmin, asignar automáticamente el id_restaurante
            if (!usuarioLogueado?.isSuperAdmin) {
                clienteConUsuario.id_restaurante = usuarioLogueado?.id_restaurante;
            }

            if (editMode) {
            await updateCliente(cliente.id_cliente, clienteConUsuario);
            alert("Cliente actualizado correctamente");
            } else {
            await createCliente(clienteConUsuario);
            alert("Cliente creado correctamente");
            }

            resetForm();
            setModalOpen(false);
            loadClientes();
        } catch (err) {
            console.error("❌ Error del backend:", err.response?.data || err.message);

            // Captura tanto el mensaje general como el error específico
            const backendMsg = err.response?.data
            ? `${err.response.data.message}: ${err.response.data.error}`
            : "Error en la solicitud.";
            setErrors({ general: backendMsg });

            // Muestra mensaje completo
            alert(backendMsg);
        }
    };

    const handleEdit = (c) => {
        setCliente(c);
        setEditMode(true);
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar este cliente?")) {
        await deleteCliente(id);
        loadClientes();
        }
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-center"> Gestión de Clientes </h1>
        
        {/* Buscador + botón */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <input type="text" placeholder="🔍 Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
            <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"> ➕ Nuevo Cliente </button>
        </div>

        {/* Tabla */}
        <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
            <table className="min-w-full text-sm text-left divide-y divide-gray-200">
            <thead className="bg-gray-100 text-gray-700 font-semibold">
                <tr className="cursor-pointer">
                <SortableHeader label="N" sortKey="id_cliente" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Nombre" sortKey="nombre" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Identificación" sortKey="identificacion" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Teléfono" sortKey="telefono" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Correo" sortKey="correo" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                {usuarioLogueado?.isSuperAdmin && (
                    <SortableHeader label="Restaurante" sortKey="restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                )}
                <SortableHeader label="Tipo Cliente" sortKey="tipo_cliente" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <th className="px-4 py-2 text-center">Acciones</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
                {currentClientes.length > 0 ? (
                currentClientes.map((c, idx) => (
                    <tr key={c.id_cliente} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                    <td className="px-4 py-2">{c.nombre}</td>
                    <td className="px-4 py-2">{c.identificacion}</td>
                    <td className="px-4 py-2">{c.telefono || "—"}</td>
                    <td className="px-4 py-2">{c.correo || "—"}</td>
                    {usuarioLogueado?.isSuperAdmin && (
                        <td className="px-4 py-2">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                {c.restaurante?.nombre_comercial || "Sin restaurante"}
                            </span>
                        </td>
                    )}
                    <td className="px-4 py-2">{c.tipo_cliente}</td>
                    <td className="px-4 py-2 text-center flex justify-center gap-2">
                    <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(c)}>Editar</button>
                    <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(c.id_cliente)}>Eliminar</button>
                    </td>
                    </tr>
                ))
                ) : (
                <tr><td colSpan={usuarioLogueado?.isSuperAdmin ? "8" : "7"} className="text-center py-4 text-gray-500"> No hay clientes registrados</td></tr>
                )}
            </tbody>
            </table>
        </div>

        {/* Paginación */}
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

        {/* Modal Crear / Editar */}
        {modalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg w-[70%] max-h-[90vh] overflow-y-auto px-10 py-6 relative">
                <button onClick={() => setModalOpen(false)} className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl">✖</button>
                <h2 className="text-2xl font-semibold mb-6 text-center sticky top-0 bg-white pb-2">{editMode ? "Editar Cliente" : "Nuevo Cliente"}</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputGroup label="Nombre" error={errors.nombre}>
                    <input name="nombre" value={cliente.nombre} onChange={handleChange} placeholder="Ingrese nombre completo" className={`input-base ${errors.nombre ? "border-red-500 focus:ring-red-400" : ""}`} ref={(el) => (inputRefs.current["nombre"] = el)} />
                    </InputGroup>
                    
                    <InputGroup label="Dirección" error={errors.direccion}>
                    <input name="direccion" value={cliente.direccion || ""} onChange={handleChange} placeholder="Ingrese dirección" className="input-base" ref={(el) => (inputRefs.current["direccion"] = el)} />
                    </InputGroup>

                    <InputGroup label="Tipo Identificación" error={errors.tipo_identificacion}>
                    <select name="tipo_identificacion" value={cliente.tipo_identificacion} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["tipo_identificacion"] = el)}>
                        <option value="05">Cédula</option>
                        <option value="04">RUC</option>
                        <option value="06">Pasaporte</option>
                    </select>
                    </InputGroup>

                    <InputGroup label="Identificación" error={errors.identificacion}>
                    <input name="identificacion" value={cliente.identificacion} onChange={handleChange} placeholder="Ingrese número de identificación" className={`input-base ${errors.identificacion ? "border-red-500 focus:ring-red-400" : ""}`} ref={(el) => (inputRefs.current["identificacion"] = el)} />
                    </InputGroup>

                    <InputGroup label="Teléfono" error={errors.telefono}>
                    <input name="telefono" value={cliente.telefono || ""} onChange={handleChange} placeholder="Ingrese teléfono" className="input-base" ref={(el) => (inputRefs.current["telefono"] = el)} />
                    </InputGroup>

                    <InputGroup label="Correo" error={errors.correo}>
                    <input name="correo" value={cliente.correo || ""} onChange={handleChange} placeholder="correo@ejemplo.com" className="input-base" ref={(el) => (inputRefs.current["correo"] = el)} />
                    </InputGroup>

                    {/* Campo Restaurante - Solo visible para SuperAdmin */}
                    {usuarioLogueado?.isSuperAdmin && (
                        <InputGroup label="Restaurante" error={errors.id_restaurante}>
                        <select name="id_restaurante" value={cliente.id_restaurante} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["id_restaurante"] = el)}>
                            <option value="">Seleccione un restaurante</option>
                            {restaurantes.map(r => (<option key={r.id_restaurante} value={r.id_restaurante}>{r.nombre_comercial}</option>))}
                        </select>
                        </InputGroup>
                    )}

                    <InputGroup label="Tipo Cliente" error={errors.tipo_cliente}>
                    <select name="tipo_cliente" value={cliente.tipo_cliente} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["tipo_cliente"] = el)}>
                        <option value="registrado">Registrado</option>
                        <option value="invitado">Invitado</option>
                    </select>
                    </InputGroup>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                    <button type="button" onClick={() => setModalOpen(false)} className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded">Cancelar</button>
                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded">{editMode ? "Actualizar" : "Guardar"}</button>
                </div>
                </form>
            </div>
            </div>
        )}
        </div>
    );
    };

export default ClientesPage;
